import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Supabase environment variables are missing!')
}

export const supabase = createClient(supabaseUrl || '', supabaseAnonKey || '', {
  auth: {
    autoRefreshToken: true,
    persistSession: true
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  }
})

export const chatHelpers = {
  async getConversations(clinicIds = null, { maxDaysAgo = 2, from = 0, size = 30 } = {}) {
    try {
      const cutoffDate = new Date()
      cutoffDate.setDate(cutoffDate.getDate() - maxDaysAgo)

      let query = supabase
        .from('clinic_conversations')
        .select('id,clinic_id,patient_phone,patient_name,last_message,last_message_time,last_message_direction,needs_attention,unread_count')
        .gte('last_message_time', cutoffDate.toISOString())
        .order('last_message_time', { ascending: false })
        .range(from, from + size - 1)

      if (clinicIds && Array.isArray(clinicIds) && clinicIds.length > 0) {
        query = query.in('clinic_id', clinicIds)
      }

      const { data, error } = await query
      if (error) throw error
      return data || []
    } catch (error) {
      console.error('[Yapp] Error fetching conversations:', error)
      throw error
    }
  },

  async getMessages(conversationId, { limit = 20, before = null } = {}) {
    try {
      let query = supabase
        .from('clinic_messages')
        .select('id,conversation_id,clinic_id,patient_phone,content,direction,twilio_sid,twilio_status,created_at')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: false })
        .limit(limit)

      if (before) {
        query = query.lt('created_at', before)
      }

      const { data, error } = await query
      if (error) throw error
      return (data || []).reverse()
    } catch (error) {
      console.error('Error fetching messages:', error)
      throw error
    }
  },

  async sendMessage(conversationId, clinicId, patientPhone, content, twilioSid = null) {
    try {
      const { data, error } = await supabase
        .from('clinic_messages')
        .insert({
          conversation_id: conversationId,
          clinic_id: clinicId,
          patient_phone: patientPhone,
          content: content,
          direction: 'outgoing',
          twilio_sid: twilioSid
        })
        .select()
        .single()

      if (error) throw error
      return data
    } catch (error) {
      console.error('Error sending message:', error)
      throw error
    }
  },

  async markAsRead(conversationId) {
    try {
      const { error } = await supabase
        .from('clinic_conversations')
        .update({ unread_count: 0 })
        .eq('id', conversationId)

      if (error) throw error
    } catch (error) {
      console.error('Error marking as read:', error)
      throw error
    }
  },

  async getOrCreateConversation(clinicId, patientPhone, patientName = null) {
    try {
      const { data: existing } = await supabase
        .from('clinic_conversations')
        .select('*')
        .eq('clinic_id', clinicId)
        .eq('patient_phone', patientPhone)
        .single()

      if (existing) return existing

      const { data: newConv, error: createError } = await supabase
        .from('clinic_conversations')
        .insert({
          clinic_id: clinicId,
          patient_phone: patientPhone,
          patient_name: patientName || 'Paciente'
        })
        .select()
        .single()

      if (createError) throw createError
      return newConv
    } catch (error) {
      console.error('Error getting/creating conversation:', error)
      throw error
    }
  },

  async updateConversationAttention(clinicId, patientPhone, needsAttention) {
    try {
      const { data, error } = await supabase
        .rpc('update_conversation_attention', {
          p_clinic_id: clinicId,
          p_patient_phone: patientPhone,
          p_needs_attention: needsAttention
        })

      if (error) throw error
      if (!data.success) {
        throw new Error(data.error || 'Failed to update conversation attention')
      }
      return data
    } catch (error) {
      console.error('Error updating conversation attention:', error)
      throw error
    }
  }
}

export const realtimeSubscriptions = {
  subscribeToMessages(conversationId, callback) {
    const channelName = `messages:${conversationId}`
    const channel = supabase.channel(channelName)

    channel.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'clinic_messages',
        filter: `conversation_id=eq.${conversationId}`
      },
      (payload) => callback(payload)
    )

    channel.subscribe()
    return channel
  },

  subscribeToConversations(clinicId, callback) {
    const channelName = `conversations:${clinicId || 'all'}`
    const channel = supabase.channel(channelName)

    channel.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'clinic_conversations',
        filter: clinicId ? `clinic_id=eq.${clinicId}` : undefined
      },
      (payload) => callback(payload)
    )

    channel.subscribe()
    return channel
  },

  unsubscribe(channel) {
    if (channel) {
      supabase.removeChannel(channel)
    }
  }
}

export default supabase
